/**
 * Date Formatting Utilities
 * All dates are formatted to IST (Indian Standard Time)
 */

const IST_TIMEZONE = "Asia/Kolkata";

/**
 * Format date to IST with short month, day, year and time
 * Example: "Dec 2, 2025, 3:45 PM"
 */
export const formatDateTime = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      timeZone: IST_TIMEZONE,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Format date to IST with short month, day, and year only
 * Example: "Dec 2, 2025"
 */
export const formatDate = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      timeZone: IST_TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Format time only in IST
 * Example: "3:45 PM"
 */
export const formatTime = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
      timeZone: IST_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Format time with seconds in IST
 * Example: "3:45:30 PM"
 */
export const formatTimeWithSeconds = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
      timeZone: IST_TIMEZONE,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Format full date and time with seconds in IST
 * Example: "Dec 2, 2025, 3:45:30 PM"
 */
export const formatDateTimeWithSeconds = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      timeZone: IST_TIMEZONE,
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Format date with medium style in IST
 * Example: "Dec 2, 2025, 3:45 PM"
 */
export const formatDateTimeMedium = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      timeZone: IST_TIMEZONE,
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(dateString);
  }
};

/**
 * Get relative time (e.g., "2m ago", "3h ago", "5d ago")
 * Falls back to formatted date for older dates
 */
export const formatRelativeTime = (dateString: string | Date): string => {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateTime(dateString);
  } catch {
    return String(dateString);
  }
};
